import React, {useMemo} from 'react';
import {Message, Button} from 'rsuite';
import {useCartQuery} from '@/api/useCartQuery';
import {useUpdateCart} from '@/api/useUpdateCart';
import CartItem from '@/components/cart-item/CartItem';
import {useCurrenciesStore} from '@/state/currenciesStore';
import {convertCurrencyAmount, formatCurrency} from '@/utils/currencyUtils';

export type CartSidebarProps = {
  showCartTitle?: boolean;
};

export default function CartSidebar({showCartTitle = true}: CartSidebarProps) {
  const cartQuery = useCartQuery();
  const updateQty = useUpdateCart();
  const removeItem = useUpdateCart();

  const isLoading = cartQuery.isLoading || updateQty.isPending || removeItem.isPending;
  const error = (cartQuery.error as Error | null) || (updateQty.error as Error | null) || (removeItem.error as Error | null);
  const cart = cartQuery.data;

  // Selected payment currency on the cart and conversion rates from store
  const currency = cart?.paymentCurrency;
  const rates = useCurrenciesStore((s) => s.rates);

  // Convert and format total using selected payment currency
  const convertedTotal = useMemo(() => convertCurrencyAmount(cart?.total ?? 0, rates, 'NOK', currency), [cart?.total, rates, currency]);
  const totalFormatted = useMemo(() => formatCurrency(convertedTotal, currency), [convertedTotal, currency]);

  // Shipping cost and VAT (base NOK from cart totals)
  const shippingBaseNok = useMemo(() => Number((cart as any)?.totalShippingCost) || 0, [cart?.totalShippingCost]);
  const vatBaseNok = useMemo(() => Number((cart as any)?.totalTax) || 0, [cart?.totalTax]);

  const convertedShipping = useMemo(() => convertCurrencyAmount(shippingBaseNok, rates, 'NOK', currency), [shippingBaseNok, rates, currency]);
  const shippingFormatted = useMemo(() => formatCurrency(convertedShipping, currency), [convertedShipping, currency]);

  const convertedVat = useMemo(() => convertCurrencyAmount(vatBaseNok, rates, 'NOK', currency), [vatBaseNok, rates, currency]);
  const vatFormatted = useMemo(() => formatCurrency(convertedVat, currency), [convertedVat, currency]);

  return (
    <div className="relative theme-background">
      {showCartTitle && (
        <div className="px-4 pt-4 text-xl font-bold tracking-wider text-center uppercase">
          Cart {cart?.lineItems && cart.lineItems.length > 0 ? `(${totalFormatted})` : ''}
        </div>
      )}

      {isLoading && cart?.lineItems?.length === 0 && (
        <div className="px-4 py-4 text-center text-sm">Loading cart…</div>
      )}

      {error && (
        <div className="px-4 py-2">
          <Message type="error" showIcon>
            {String((error as Error).message || error)}
          </Message>
        </div>
      )}

      {cart && cart.lineItems && cart.lineItems.length > 0 ? (
        <>
          <div className="px-4 py-2 space-y-2">
            {cart.lineItems.map((item) => (
              <CartItem
                key={String(item.id)}
                item={item}
                isUpdating={updateQty.isPending}
                isRemoving={removeItem.isPending}
                onChangeQty={(qty) => updateQty.mutate({lineItems: {[item.id]: {qty}}})}
                onRemove={() => removeItem.mutate({ lineItems: { [item.id]: { qty: 0 } } } as any)}
              />
            ))}
          </div>

          <div className='px-4 py-4 border-t  border-gray-300 border-dashed'>
            <div className="flex justify-between font-bold pt-2 text-xl uppercase">
              <span>Shipping cost</span>
              <span>{shippingFormatted}</span>
            </div>
            <div className="flex justify-between font-bold pt-2 text-xl uppercase">
              <span>VAT</span>
              <span>{vatFormatted}</span>
            </div>
            <div className="flex justify-between font-bold pt-2 text-xl uppercase">
              <span>Total</span>
              <span>{cart?.lineItems && cart.lineItems.length > 0 ? totalFormatted : ''}</span>
            </div>
          </div>
        </>
      ) : (
        !isLoading && <div className="px-4 py-4 text-center text-sm">Your cart is empty.</div>
      )}
    </div>
  );
}
